import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, User2, Share2 } from "lucide-react";
import { articles } from "../articles";
import { CONTACT_URL } from "../content";
import { track } from "../lib/analytics";
import StarField from "../components/StarField";

function Block({ b }) {
  if (b.type === "video") {
    return (
      <div className="my-10 overflow-hidden border border-white/10 bg-black aspect-video">
        <iframe
          className="h-full w-full"
          src={b.src}
          title={b.title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }
  if (b.type === "h2") {
    return (
      <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-14 mb-5 leading-tight">
        {b.text}
      </h2>
    );
  }
  if (b.type === "callout") {
    return (
      <aside className="relative my-12 border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-6 md:p-8">
        <span className="absolute -top-px -left-px h-3 w-3 border-t border-l border-[var(--primary)]" />
        <span className="absolute -bottom-px -right-px h-3 w-3 border-b border-r border-[var(--primary)]" />
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-3">
          // {b.label}
        </div>
        <p className="text-white/85 text-base md:text-lg leading-relaxed">
          {b.text}
        </p>
      </aside>
    );
  }
  return (
    <p className="text-white/70 text-base md:text-[17px] leading-[1.85] mb-6">
      {b.text}
    </p>
  );
}

export default function ArticlePage({ lang, setLang, NavbarComponent }) {
  const { slug } = useParams();
  const article = articles[slug];
  const [shared, setShared] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <div className="font-mono text-xs tracking-[0.3em] text-[var(--primary)] mb-4">
          // 404 · SIGNAL LOST
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-black mb-6">
          Article not found
        </h1>
        <Link
          to="/"
          data-testid="article-not-found-home"
          className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-xs tracking-[0.25em] uppercase hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Home
        </Link>
      </div>
    );
  }

  const a = article[lang] || article.EN;

  const handleContactClick = () => {
    track("contact_click");
    track("book_demo");
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.share) {
        await navigator.share({ title: a.title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
      setShared(true);
      window.setTimeout(() => setShared(false), 1800);
    } catch (e) {
      /* user cancelled */
    }
  };

  return (
    <div data-testid="article-root" className="min-h-screen bg-[#030508] text-white">
      {NavbarComponent}

      <article className="pt-28 md:pt-32 pb-24">
        {/* Hero */}
        <header
          data-testid="article-header"
          className="relative overflow-hidden border-b border-white/5 mb-14"
        >
          <div
            className="absolute inset-0 opacity-30 bg-cover bg-center"
            style={{ backgroundImage: `url(${article.image})` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#030508]/60 via-[#030508]/80 to-[#030508]" />
          <StarField count={40} />

          <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <Link
              to="/#news"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--primary)] transition-colors mb-8"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {a.backToNews}
            </Link>

            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--primary)] mb-5">
              // {a.category}
            </div>

            <h1
              data-testid="article-title"
              className="font-display font-black uppercase leading-[1.05] text-3xl md:text-5xl lg:text-6xl text-white"
            >
              {a.title}
            </h1>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[11px] tracking-[0.25em] uppercase text-white/50">
              <span className="inline-flex items-center gap-2">
                <User2 className="h-3.5 w-3.5" />
                {a.author}
              </span>
              <span className="h-1 w-1 rounded-full bg-white/30" />
              <time dateTime={a.date} data-testid="article-date">
                {a.dateLabel}
              </time>
              <span className="h-1 w-1 rounded-full bg-white/30" />
              <span className="inline-flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" />
                {a.readTime}
              </span>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p
            data-testid="article-lead"
            className="text-white/85 text-lg md:text-xl leading-[1.7] mb-10 pb-10 border-b border-white/10"
          >
            {a.lead}
          </p>

          <div data-testid="article-body">
            {a.blocks.map((b, i) => (
              <Block key={i} b={b} />
            ))}
          </div>

          {/* Footer actions */}
          <div className="mt-14 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <button
              data-testid="article-share"
              onClick={handleShare}
              className="inline-flex items-center gap-2 border border-white/20 text-white font-mono text-[11px] tracking-[0.25em] uppercase font-semibold px-5 py-3 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
            >
              <Share2 className="h-4 w-4" />
              {shared ? (lang === "ES" ? "Copiado" : "Copied") : a.shareLabel}
            </button>
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-cta"
              onClick={handleContactClick}
              className="group inline-flex items-center gap-3 bg-[var(--primary)] text-black font-mono text-xs tracking-[0.25em] uppercase font-semibold px-7 py-4 hover:bg-[var(--primary-hover)] transition-colors"
            >
              {a.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
